"use client";
import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, X, ChevronDown } from "lucide-react";
import { usePathname } from "next/navigation";

const navLinks = [
  { href: "/", label: "HOME", id: "hero" },
  {
    href: "/#services",
    label: "SERVICES +",
    id: "services",
    dropdown: [
      {
        href: "/#residential-solar",
        label: "Residential Solar",
        id: "residential-solar",
      },
      {
        href: "/#commercial-solar",
        label: "Commercial Solar",
        id: "commercial-solar",
      },
      {
        href: "/#housing-society-solar",
        label: "Housing Society Solar",
        id: "housing-society-solar",
      },
      {
        href: "/#carport-mounting-structures",
        label: "Carport Mounting Structures",
        id: "carport-mounting-structures",
      },
    ],
  },
  
  // { href: "/careers", label: "CAREERS", id: "careers" },
  // { href: "/finance", label: "FINANCE", id: "finance" },
  { href: "/maintenance", label: "MAINTENANCE", id: "maintenance" },
  { href: "/about", label: "ABOUT", id: "about" },
  { href: "/blogs", label: "BLOGS", id: "blogs" },
  { href: "/calculator", label: "SOLAR CALCULATOR", id: "calculator" },
];

export const navLinks1 = [
  {
    title: "EARN WITH US +",
    href: "#",
    key: "earn",
    dropdownOpenState: "dropdownOpenearn",
    dropdown: [
      { title: "ECO PARTNERS", href: "/eco-partner", key: "solar" },
      {
        title: "CHANNEL PARTNERS",
        href: "/channelpartners",
        key: "channelpartners",
      },
    ],
  },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownOpenearn, setDropdownOpenearn] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [mobileDropdownOpenearn, setMobileDropdownOpenearn] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [activeLink, setActiveLink] = useState("hero");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  const isServiceActive = () => {
    const serviceIds = navLinks
      .find((link) => link.id === "services")
      ?.dropdown?.map((item) => item.id);
    return serviceIds?.includes(activeLink);
  };

  useEffect(() => {
    const currentHash = window.location.hash.replace("#", "");

    if (pathname === "/" && currentHash) {
      setActiveLink(currentHash);
    } else if (pathname !== "/") {
      setActiveLink(pathname.replace("/", ""));
    } else {
      setActiveLink("hero");
    }
  }, [pathname]);

  useEffect(() => {
    const sections = document.querySelectorAll("section");
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveLink(entry.target.id);
        }
      });
    }, observerOptions);

    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  useEffect(() => {
    if (menuOpen || dropdownOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [menuOpen, dropdownOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  const handleDropdownClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();

    if (pathname !== "/") {
      window.location.href = href;
    } else {
      window.location.href = href;
    }

    setDropdownOpen(false);
    setMenuOpen(false);
  };

  const dropdownRefEarn = useRef<HTMLDivElement | null>(null);

  return (
    <nav
      className={`fixed top-0 left-0 w-full bg-pri flex justify-between items-center h-[80px] px-8 shadow-md z-50 transition-transform duration-300 py-4 ${
        isVisible ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      {/* Logo */}
      <Link rel="preload" href="/" className="flex flex-col items-center gap-1">
        <Image src="/logo.png" alt="Logo Image" width={54} height={54} />
        <div className="text-white text-head1 text-desc-size3 font-sans tracking-[.1em] font-semibold">
        SATYANAM SOLAR
        </div>
      </Link>

      {/* Desktop Navigation */}
      <div className="hidden lg:flex gap-2 lg:gap-[3vw] text-white text-desc-size3 font-robo tracking-[.12em] items-center">
        {navLinks.map((link) => (
          <div
            key={link.href}
            className="relative"
            ref={link.dropdown ? dropdownRef : null}
          >
            {link.id === "services" ? (
              <>
                <div
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                  className="relative"
                >
                  <button
                    className={`p-2 flex items-center gap-1 nav-link font-semibold ${
                      isServiceActive() ? "text-orange-500" : ""
                    }`}
                  >
                    {link.label}
                  </button>
                  {dropdownOpen && (
                    //purple dropdown for services
                    <div className="flex flex-col gap-2 absolute top-full w-60 h-40 justify-center shadow-md items-center z-50 rounded-md bg-pri">
                      {link?.dropdown?.map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          className={`flex px-0 py-1 nav-link ${
                            activeLink === item.id ? "text-yellow-400" : ""
                          } hover:text-orange-500`}
                          onClick={(e) => handleDropdownClick(e, item?.href)}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link
                href={link.href}
                className={`p-2 font-semibold ${
                  link.id === "calculator" 
                  ? "py-3 bg-yellow-500 text-black" 
                  :activeLink === link.id ? "nav-link text-yellow-500" : ""
                }`}
              >
                {link.label}
              </Link>
            )}
          </div>
        ))}
        {navLinks1.map((navItem) => (
          <div
            key={navItem.key}
            className="relative"
            ref={dropdownRefEarn}
            onMouseEnter={() => setDropdownOpenearn(true)}
            onMouseLeave={() => setDropdownOpenearn(false)}
          >
            <Link
              href={navItem.href}
              className={`p-2 nav-link font-semibold ${
                activeLink === navItem.key ||
                pathname === "/channelpartners" ||
                pathname === "/eco-partner"
                  ? "text-yellow-500"
                  : ""
              }`}
            >
              {navItem.title}
            </Link>

            {dropdownOpenearn && navItem.dropdown && (
              //purple dropdown for earn with us
              <div className="flex flex-col gap-2 right-2 absolute top-full w-60 h-20 justify-center shadow-md items-center mt-2 z-50 rounded-md bg-pri">
                {navItem.dropdown.map((dropItem) => (
                  <Link
                    key={dropItem.key}
                    href={dropItem.href}
                    className={`flex px-0 py-1 nav-link  ${
                      activeLink === dropItem.key ? "text-yellow-500" : ""
                    } hover:text-orange-500`}
                    onClick={() => {
                      setActiveLink(dropItem.key);
                      setDropdownOpenearn(false);
                    }}
                  >
                    {dropItem.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Overlay */}
      <div
        className={`fixed top-0 left-0 w-full h-screen bg-black bg-opacity-60 z-30 transition-opacity duration-300 ${
          menuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setMenuOpen(false)}
      ></div>

      {/* Mobile Menu Button */}
      <button
        aria-label="Menu"
        className="lg:hidden p-2 z-50 text-white"
        onClick={() => setMenuOpen((prev) => !prev)}
      >
        {menuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile Menu Content */}
      <div
        className={`fixed top-0 right-0 w-full md:w-1/2 h-screen bg-pri shadow-lg flex flex-col items-center justify-center gap-6 text-lg text-white text-desc-size2 font-robo tracking-[.12em] z-40 transition-transform duration-300 ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <Link href="/" className="flex flex-col items-center gap-1" onClick={() => setMenuOpen(false)}>
          <Image rel="preload" src="/logo.png" alt="Logo Image" width={40} height={40} />
          <div className="text-head1 text-desc-size3 font-sans tracking-[.18em] font-semibold">
            SATYANAM SOLAR
          </div>
        </Link>

        {navLinks.map((link) => (
          <div key={link.href} className="flex flex-col items-center">
            {link.dropdown ? (
              <>
                <button
                  onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                  className={`p-0 flex items-center gap-1 nav-link text-desc-size2 font-semibold ${
                    isServiceActive() ? "text-pri" : ""
                  }`}
                >
                  {link.label}
                </button>
                {mobileDropdownOpen && (
                  <div className="flex flex-col items-center mt-2">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={`p-0 nav-link text-desc-size3 ${
                          activeLink === item.id ? "text-yellow-500" : ""
                        }`}
                        onClick={(e) => handleDropdownClick(e, item.href)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Link
                href={link.href}
                className={`p-0 text-desc-size2 font-semibold ${
                  link.id === "calculator" 
                  ? "p-2 bg-yellow-500 text-black" 
                  :activeLink === link.id ? "nav-link text-yellow-500" : ""
                }`}
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </Link>
            )}
          </div>
        ))}

        {/* Add "EARN WITH US +" dropdown to mobile menu */}
        {navLinks1.map((navItem) => (
          <div key={navItem.key} className="flex flex-col items-center">
            <button
              onClick={() => setMobileDropdownOpenearn(!mobileDropdownOpenearn)}
              className={`p-0 flex items-center gap-1 nav-link text-desc-size2 font-semibold ${
                activeLink === navItem.key ? "text-yellow-500" : ""
              }`}
            >
              {navItem.title}
            </button>
            {mobileDropdownOpenearn && (
              <div className="flex flex-col items-center mt-2">
                {navItem.dropdown.map((dropItem) => (
                  <Link
                    key={dropItem.key}
                    href={dropItem.href}
                    className={`p-0 nav-link text-desc-size3 ${
                      activeLink === dropItem.key ? "text-yellow-500" : ""
                    }`}
                    onClick={() => {
                      setActiveLink(dropItem.key);
                      setMenuOpen(false);
                    }}
                  >
                    {dropItem.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </nav>
  );
};

export default Navbar;